import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { titlesResponseSchema } from '@unit/shared';
import { useApi } from '../../lib/useApi.js';

/** The "other" option's value: a title is trimmed and non-empty, so a leading space can never collide with one. */
const OTHER = ' other';

/**
 * The job title (תפקיד) as a pick from the company's list (owner,
 * 2026-09-19, story 38), with "other" opening a text field for a title the
 * list does not have yet. Whatever is saved on the user is remembered by the
 * server, so the next picker offers it — there is no separate list to
 * manage.
 *
 * `value` is the title itself (or '' for none), so the form owning it saves
 * exactly what it would have saved with a plain input.
 */
export function TitlePicker({
  value,
  onChange,
  className,
  testId = 'title-picker',
}: {
  value: string;
  onChange: (title: string) => void;
  className: string;
  testId?: string;
}) {
  const { t } = useTranslation();
  const titles = useApi('/titles', titlesResponseSchema);
  const known = titles.kind === 'ready' ? titles.data.titles : [];
  const [other, setOther] = useState(false);
  // A value the list lacks — typed under "other", or set before the list
  // existed — keeps the text field open rather than snapping to "none".
  const showOther = other || (value !== '' && titles.kind === 'ready' && !known.includes(value));
  const selected = showOther ? OTHER : value;

  return (
    <>
      <select
        className={className}
        value={selected}
        onChange={(e) => {
          if (e.target.value === OTHER) {
            setOther(true);
            onChange('');
          } else {
            setOther(false);
            onChange(e.target.value);
          }
        }}
        data-testid={testId}
      >
        <option value="">{t('users.titleNone')}</option>
        {known.map((title) => (
          <option key={title} value={title}>
            {title}
          </option>
        ))}
        {/* Before the list arrives a preset value still needs its option, or the select shows "none". */}
        {value !== '' && !showOther && !known.includes(value) && <option value={value}>{value}</option>}
        <option value={OTHER}>{t('users.titleOther')}</option>
      </select>
      {showOther && (
        <input
          className={`${className} mt-1`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={t('users.newTitle')}
          aria-label={t('users.newTitle')}
          data-testid={`${testId}-other`}
        />
      )}
    </>
  );
}
